// Firebase Messaging Service for Provider-Client Direct Messages
import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  getDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
  writeBatch,
  limit,
  increment
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  DirectMessage,
  Conversation,
  TypingIndicator,
  SendMessageRequest,
  CreateConversationRequest,
  MessagesCallback,
  ConversationsCallback,
  TypingCallback,
  UnsubscribeFunction
} from '../../types/messaging';

class FirebaseMessagingService {
  private static instance: FirebaseMessagingService;
  private conversationsCollection = 'conversations';
  private messagesSubcollection = 'messages';
  private typingCollection = 'typingIndicators';

  static getInstance(): FirebaseMessagingService {
    if (!FirebaseMessagingService.instance) {
      FirebaseMessagingService.instance = new FirebaseMessagingService();
    }
    return FirebaseMessagingService.instance;
  }

  // ============ CONVERSATION METHODS ============

  /**
   * Create a new conversation between provider and client
   */
  async createConversation(request: CreateConversationRequest): Promise<string> {
    try {
      // Check if conversation already exists
      const existingConversation = await this.findExistingConversation(
        request.providerId,
        request.clientId
      );

      if (existingConversation) {
        return existingConversation.id;
      }

      // Create new conversation
      const conversationData = {
        providerId: request.providerId,
        providerName: request.providerName,
        clientId: request.clientId,
        clientName: request.clientName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessage: null,
        unreadCount: {
          provider: 0,
          client: 0
        },
        isArchived: false
      };

      const docRef = await addDoc(
        collection(db, this.conversationsCollection),
        conversationData
      );

      // If there's an initial message, send it (respects actual sender role)
      if (request.initialMessage) {
        const senderIsProvider = (request.initialMessageSenderRole || 'provider') === 'provider';
        await this.sendMessage({
          conversationId: docRef.id,
          senderId: senderIsProvider ? request.providerId : request.clientId,
          senderName: senderIsProvider ? request.providerName : request.clientName,
          senderRole: senderIsProvider ? 'provider' : 'client',
          recipientId: senderIsProvider ? request.clientId : request.providerId,
          recipientName: senderIsProvider ? request.clientName : request.providerName,
          content: request.initialMessage
        });
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  /**
   * Update provider on all conversations for a client (after patient transfer).
   * Finds conversations where clientId matches and updates providerId/providerName.
   */
  async reassignConversationsToProvider(
    clientId: string,
    newProviderId: string,
    newProviderName: string
  ): Promise<void> {
    try {
      const q = query(
        collection(db, this.conversationsCollection),
        where('clientId', '==', clientId)
      );
      const snapshot = await getDocs(q);
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        if (data.providerId !== newProviderId) {
          await updateDoc(doc(db, this.conversationsCollection, docSnap.id), {
            providerId: newProviderId,
            providerName: newProviderName,
            updatedAt: serverTimestamp(),
          });
        }
      }
    } catch (error) {
      console.error('[Messaging] Failed to reassign conversations:', error);
    }
  }

  /**
   * Find existing conversation between provider and client.
   * Throws on error to prevent duplicate conversation creation.
   */
  async findExistingConversation(
    providerId: string,
    clientId: string
  ): Promise<Conversation | null> {
    const q = query(
      collection(db, this.conversationsCollection),
      where('providerId', '==', providerId),
      where('clientId', '==', clientId),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const docSnap = snapshot.docs[0];
    return this.convertToConversation(docSnap.id, docSnap.data());
  }

  /**
   * Get a conversation by ID
   */
  async getConversation(conversationId: string): Promise<Conversation | null> {
    try {
      const docRef = doc(db, this.conversationsCollection, conversationId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.convertToConversation(docSnap.id, docSnap.data());
    } catch (error) {
      console.error('Error getting conversation:', error);
      return null;
    }
  }

  /**
   * Subscribe to conversations for a user (provider or client)
   */
  subscribeToConversations(
    userId: string,
    userRole: 'provider' | 'client',
    callback: ConversationsCallback
  ): UnsubscribeFunction {
    const fieldName = userRole === 'provider' ? 'providerId' : 'clientId';

    // Don't filter isArchived in Firestore query — documents without the field
    // would be excluded by Firestore (field must exist for == false to match).
    // Filter client-side instead to catch all conversations.
    const q = query(
      collection(db, this.conversationsCollection),
      where(fieldName, '==', userId),
      orderBy('updatedAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const conversations = snapshot.docs
        .map((doc) => this.convertToConversation(doc.id, doc.data()))
        .filter((conv) => !conv.isArchived);
      callback(conversations);
    }, (error) => {
      // Log as warning — may be a missing Firestore index (click the link in the error to create it)
      console.warn('Error subscribing to conversations:', error);
      callback([]);
    });
  }

  /**
   * Archive a conversation
   */
  async archiveConversation(
    conversationId: string,
    archivedBy: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.conversationsCollection, conversationId);
      await updateDoc(docRef, {
        isArchived: true,
        archivedAt: serverTimestamp(),
        archivedBy
      });
    } catch (error) {
      console.error('Error archiving conversation:', error);
      throw error;
    }
  }

  /**
   * Unarchive a conversation
   */
  async unarchiveConversation(conversationId: string): Promise<void> {
    try {
      const docRef = doc(db, this.conversationsCollection, conversationId);
      await updateDoc(docRef, {
        isArchived: false,
        archivedAt: null,
        archivedBy: null
      });
    } catch (error) {
      console.error('Error unarchiving conversation:', error);
      throw error;
    }
  }

  // ============ MESSAGE METHODS ============

  /**
   * Send a new message
   */
  async sendMessage(request: SendMessageRequest): Promise<string> {
    try {
      const messageData = {
        conversationId: request.conversationId,
        senderId: request.senderId,
        senderName: request.senderName,
        senderRole: request.senderRole,
        recipientId: request.recipientId,
        recipientName: request.recipientName,
        content: request.content,
        timestamp: serverTimestamp(),
        read: false,
        readAt: null,
        attachments: [],
        isEdited: false,
        editedAt: null,
        isDeleted: false,
        deletedAt: null
      };

      // Add message to subcollection
      const messagesRef = collection(
        db,
        this.conversationsCollection,
        request.conversationId,
        this.messagesSubcollection
      );
      const docRef = await addDoc(messagesRef, messageData);

      // Update conversation with last message and increment unread count
      const conversationRef = doc(
        db,
        this.conversationsCollection,
        request.conversationId
      );

      const recipientRole = request.senderRole === 'provider' ? 'client' : 'provider';

      // Atomic increment prevents race condition when messages sent rapidly
      await updateDoc(conversationRef, {
        lastMessage: {
          content: request.content,
          senderId: request.senderId,
          senderRole: request.senderRole,
          timestamp: serverTimestamp()
        },
        updatedAt: serverTimestamp(),
        [`unreadCount.${recipientRole}`]: increment(1)
      });

      return docRef.id;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  /**
   * Subscribe to messages in a conversation
   */
  subscribeToMessages(
    conversationId: string,
    callback: MessagesCallback
  ): UnsubscribeFunction {
    const messagesRef = collection(
      db,
      this.conversationsCollection,
      conversationId,
      this.messagesSubcollection
    );

    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map((doc) =>
        this.convertToMessage(doc.id, doc.data())
      );
      callback(messages);
    }, (error: any) => {
      // Log once but don't spam — permission errors are handled at the context level
      if (error?.code === 'permission-denied') {
        console.warn('Messages subscription: permission denied for conversation', conversationId);
      } else {
        console.error('Error subscribing to messages:', error);
      }
      callback([]);
    });
  }

  /**
   * Mark messages as read
   */
  async markMessagesAsRead(
    conversationId: string,
    userId: string,
    userRole: 'provider' | 'client'
  ): Promise<void> {
    try {
      const messagesRef = collection(
        db,
        this.conversationsCollection,
        conversationId,
        this.messagesSubcollection
      );

      // Get unread messages for this user
      const q = query(
        messagesRef,
        where('recipientId', '==', userId),
        where('read', '==', false)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) return;

      // Batch update all unread messages
      const batch = writeBatch(db);

      snapshot.docs.forEach((docSnap) => {
        batch.update(docSnap.ref, {
          read: true,
          readAt: serverTimestamp()
        });
      });

      // Reset unread count for this user
      const conversationRef = doc(
        db,
        this.conversationsCollection,
        conversationId
      );
      batch.update(conversationRef, {
        [`unreadCount.${userRole}`]: 0
      });

      await batch.commit();
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }
  }

  /**
   * Edit a message
   */
  async editMessage(
    conversationId: string,
    messageId: string,
    newContent: string
  ): Promise<void> {
    try {
      const messageRef = doc(
        db,
        this.conversationsCollection,
        conversationId,
        this.messagesSubcollection,
        messageId
      );

      await updateDoc(messageRef, {
        content: newContent,
        isEdited: true,
        editedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error editing message:', error);
      throw error;
    }
  }

  /**
   * Delete a message (soft delete)
   */
  async deleteMessage(
    conversationId: string,
    messageId: string
  ): Promise<void> {
    try {
      const messageRef = doc(
        db,
        this.conversationsCollection,
        conversationId,
        this.messagesSubcollection,
        messageId
      );

      await updateDoc(messageRef, {
        isDeleted: true,
        deletedAt: serverTimestamp(),
        content: '[Message deleted]'
      });
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }

  // ============ TYPING INDICATOR METHODS ============

  /**
   * Set typing indicator
   */
  async setTypingIndicator(
    conversationId: string,
    userId: string,
    userName: string,
    isTyping: boolean
  ): Promise<void> {
    try {
      const typingRef = doc(
        db,
        this.typingCollection,
        `${conversationId}_${userId}`
      );

      if (isTyping) {
        await setDoc(typingRef, {
          conversationId,
          userId,
          userName,
          isTyping: true,
          timestamp: serverTimestamp()
        });
      } else {
        await deleteDoc(typingRef).catch(() => {
          // Ignore if doesn't exist
        });
      }
    } catch (error) {
      console.error('Error setting typing indicator:', error);
    }
  }

  /**
   * Subscribe to typing indicators for a conversation
   */
  subscribeToTypingIndicator(
    conversationId: string,
    currentUserId: string,
    callback: TypingCallback
  ): UnsubscribeFunction {
    const q = query(
      collection(db, this.typingCollection),
      where('conversationId', '==', conversationId)
    );

    return onSnapshot(q, (snapshot) => {
      const now = Date.now();
      const typingUsers = snapshot.docs
        .map((docSnap) => ({ ...docSnap.data() as TypingIndicator, _ref: docSnap.ref }))
        .filter((t) => {
          if (t.userId === currentUserId || !t.isTyping) return false;
          // Ignore stale indicators older than 15 seconds
          const ts = (t.timestamp as any)?.toDate?.() || t.timestamp;
          if (ts && (now - new Date(ts).getTime()) > 15000) {
            // Clean up stale indicator
            deleteDoc(t._ref).catch(() => {});
            return false;
          }
          return true;
        });

      callback(typingUsers.length > 0 ? typingUsers[0] : null);
    }, (error) => {
      console.error('Error subscribing to typing indicator:', error);
      callback(null);
    });
  }

  // ============ UTILITY METHODS ============

  /**
   * Get total unread count for a user
   */
  async getTotalUnreadCount(
    userId: string,
    userRole: 'provider' | 'client'
  ): Promise<number> {
    try {
      const fieldName = userRole === 'provider' ? 'providerId' : 'clientId';

      const q = query(
        collection(db, this.conversationsCollection),
        where(fieldName, '==', userId)
      );

      const snapshot = await getDocs(q);

      let totalUnread = 0;
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.isArchived) return;
        totalUnread += data.unreadCount?.[userRole] || 0;
      });

      return totalUnread;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Subscribe to total unread count for a user
   */
  subscribeToUnreadCount(
    userId: string,
    userRole: 'provider' | 'client',
    callback: (count: number) => void
  ): UnsubscribeFunction {
    const fieldName = userRole === 'provider' ? 'providerId' : 'clientId';

    // Filter client-side to include conversations without isArchived field
    const q = query(
      collection(db, this.conversationsCollection),
      where(fieldName, '==', userId)
    );

    return onSnapshot(q, (snapshot) => {
      let totalUnread = 0;
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.isArchived) return; // skip archived
        totalUnread += data.unreadCount?.[userRole] || 0;
      });
      callback(totalUnread);
    }, (error) => {
      console.error('Error subscribing to unread count:', error);
      callback(0);
    });
  }

  // ============ CONVERSION HELPERS ============

  private convertToConversation(id: string, data: any): Conversation {
    return {
      id,
      providerId: data.providerId,
      providerName: data.providerName,
      clientId: data.clientId,
      clientName: data.clientName,
      createdAt: this.timestampToDate(data.createdAt),
      updatedAt: this.timestampToDate(data.updatedAt),
      lastMessage: data.lastMessage
        ? {
            content: data.lastMessage.content,
            senderId: data.lastMessage.senderId,
            senderRole: data.lastMessage.senderRole,
            timestamp: this.timestampToDate(data.lastMessage.timestamp)
          }
        : undefined,
      unreadCount: data.unreadCount || { provider: 0, client: 0 },
      isArchived: data.isArchived || false,
      archivedAt: data.archivedAt
        ? this.timestampToDate(data.archivedAt)
        : undefined,
      archivedBy: data.archivedBy
    };
  }

  private convertToMessage(id: string, data: any): DirectMessage {
    return {
      id,
      conversationId: data.conversationId,
      senderId: data.senderId,
      senderName: data.senderName,
      senderRole: data.senderRole,
      recipientId: data.recipientId,
      recipientName: data.recipientName,
      content: data.content,
      timestamp: this.timestampToDate(data.timestamp),
      read: data.read || false,
      readAt: data.readAt ? this.timestampToDate(data.readAt) : undefined,
      attachments: data.attachments || [],
      isEdited: data.isEdited || false,
      editedAt: data.editedAt ? this.timestampToDate(data.editedAt) : undefined,
      isDeleted: data.isDeleted || false,
      deletedAt: data.deletedAt
        ? this.timestampToDate(data.deletedAt)
        : undefined
    };
  }

  private timestampToDate(timestamp: Timestamp | Date | null): Date {
    if (!timestamp) return new Date();
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    return timestamp;
  }
}

export default FirebaseMessagingService;
